import { Suspense, useCallback } from 'react';
import { NodeProps } from 'reactflow';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { faMobile } from '@fortawesome/pro-solid-svg-icons';
import { CreateEngageMessageParams } from 'api/engage';
import { MessageTemplateDialog } from 'assets/js/pages/engage/components/MessageTemplateDialog';
import { SaveAsTemplateAction } from 'assets/js/pages/engage/components/SaveAsTemplateAction';
import { SendSmsNodeData } from 'assets/js/pages/engage/workflows/actions/types';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import { DeleteCanvasNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DeleteCanvasNodeMenuItem';
import { DuplicateNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DuplicateNodeMenuItem';
import { SmsMessageBuilder } from 'assets/js/pages/engage/workflows/components/SmsMessageBuilder';
import { useDeleteNodeHandler } from 'assets/js/pages/engage/workflows/hooks/useDeleteNodeHandler';
import { useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import { formDefaults } from 'assets/js/pages/engage/workflows/utils/transformers';
import * as DropdownMenu from 'components/dropdown-menu';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { twMerge } from 'tailwind-merge';

interface SendSmsActionNodeProps extends NodeProps<SendSmsNodeData> {}
const SendSmsAction = ({ id, selected, data }: SendSmsActionNodeProps) => {
    const { formData } = data;
    const { updateNode, getNode } = useReactFlowStore(({ updateNode, getNode }) => ({
        updateNode,
        getNode,
    }));

    const setNodeFormData = useCallback(
        (data?: CreateEngageMessageParams) => {
            const node = getNode(id);
            if (!node || node.data?.type !== WorkflowActionTypeEnum.SEND_SMS) return;
            const nodeData: SendSmsNodeData = {
                ...node.data,
                formData: {
                    ...formDefaults,
                    isDirty: true,
                    values: {
                        internal_name: data?.internal_name ?? `Draft Text`,
                        body: data?.body ?? '',
                        message_template_id: data?.message_template_id ?? undefined,
                    },
                },
            };
            const newNode = {
                ...node,
                id,
                data: nodeData,
            };
            updateNode(id, newNode);
        },
        [getNode, updateNode, id],
    );

    const handleOnDelete = useDeleteNodeHandler({ currentNode: getNode(id) });

    const hasFormErrors = formData && Object.keys(formData.errors).length > 0;
    const isError = hasFormErrors || (!selected && !formData);

    return (
        <CanvasNode.Root isSelected={selected} isError={isError} id={id}>
            <CanvasNode.Container>
                <CanvasNode.ErrorAlert />
                <CanvasNode.Header>
                    <CanvasNode.Icon icon={faMobile} className="text-green-500" />
                    <CanvasNode.Title>Send text</CanvasNode.Title>

                    {!selected || !formData ? (
                        <DropdownMenu.Root>
                            <CanvasNode.ActionMenuTrigger />
                            <DropdownMenuContent id={id} data={data} />
                        </DropdownMenu.Root>
                    ) : null}
                </CanvasNode.Header>
                <CanvasNode.Body>
                    {formData ? (
                        <div className={twMerge(!formData && 'hidden')}>
                            <SmsMessageBuilder
                                nodeId={id}
                                selected={selected}
                                dropdownContent={<DropdownMenuContent id={id} data={data} />}
                            />
                        </div>
                    ) : (
                        <MessageTemplateDialog
                            channel="sms"
                            onTemplateSelection={setNodeFormData}
                            defaultOpen={selected && !formData}
                            visibility="workflows"
                            onOpenChange={handleOnDelete}
                        >
                            <Button className="w-full" variant="outline" color="neutral">
                                Compose message
                            </Button>
                        </MessageTemplateDialog>
                    )}
                </CanvasNode.Body>
            </CanvasNode.Container>
        </CanvasNode.Root>
    );
};

const DropdownMenuContent = ({ id, data }: { id: string; data: SendSmsNodeData }) => {
    return (
        <DropdownMenu.Portal>
            <DropdownMenu.Content align="end">
                <SaveAsTemplateAction
                    channel="sms"
                    disabled={!data.formData?.values.body || !data.formData?.values.internal_name}
                    values={{
                        body: data.formData?.values.body,
                        internal_name: data.formData?.values.internal_name,
                    }}
                />
                <DuplicateNodeMenuItem />
                <DeleteCanvasNodeMenuItem />
            </DropdownMenu.Content>
        </DropdownMenu.Portal>
    );
};

const SendSms = (props: SendSmsActionNodeProps) => {
    return (
        <Suspense fallback={<Loader />}>
            <SendSmsAction {...props} />
        </Suspense>
    );
};

export { SendSms };
export type { SendSmsActionNodeProps };
